import { HttpStatusCode } from '@angular/common/http';
import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { EmployeeProfile } from '@coin/shared/org-tree/util';
import { HttpHelpersService } from '@coin/shared/core/data-access';
import { Position } from '@coin/shared/core/util-models';
import { produce } from 'immer';
import { catchError, map, Observable, of } from 'rxjs';
import {
  Position as ApiPosition,
  EmployeePositionsService,
  EmployeeProfileService,
  EmployeeQuickProfile
} from '../../../../api/generated';
import { LoadingError } from '../../../../org-management/models/loading-error.enum';
import { MappingHelpers } from '../../../helper/mapping-helpers';

export interface QuickdrawProfileStateModel {
  employeeProfileLoadingError: LoadingError;
  employeeProfileLoading: boolean;
  employeeProfiles: { [employeeId: string]: EmployeeProfile };
  positionsLoading: boolean;
  positions: { [employeeId: string]: Position[] };
}

const STATE_DEFAULTS: QuickdrawProfileStateModel = {
  employeeProfileLoadingError: undefined,
  employeeProfileLoading: false,
  employeeProfiles: {},
  positionsLoading: false,
  positions: {}
};

// Auto-migrated from NGXS
@Injectable({ providedIn: 'root' })
export class QuickdrawProfileState {
  private readonly employeeProfileService = inject(EmployeeProfileService);
  private readonly employeePositionsService = inject(EmployeePositionsService);
  private readonly httpHelper = inject(HttpHelpersService);

  private readonly quickdrawProfileStateModel = signal<QuickdrawProfileStateModel>(STATE_DEFAULTS);

  public employeeProfileLoading = computed(() => this.quickdrawProfileStateModel().employeeProfileLoading);

  public employeeProfileLoadingError = computed(() => this.quickdrawProfileStateModel().employeeProfileLoadingError);

  public readonly employeeProfiles = computed(() => this.quickdrawProfileStateModel().employeeProfiles);

  public readonly positions = computed(() => this.quickdrawProfileStateModel().positions);

  public employeeProfile(employeeId: string): Signal<EmployeeProfile> {
    return computed(() => this.quickdrawProfileStateModel().employeeProfiles[employeeId]);
  }

  public employeePrimaryOrgId(employeeId: string): Signal<string> {
    return computed(() => this.quickdrawProfileStateModel().employeeProfiles[employeeId]?.employee?.orgId);
  }

  public otherEmployeePositions(employeeId: string, orgId: string): Signal<Position[]> {
    return computed(() => {
      const state = this.quickdrawProfileStateModel();
      return orgId
        ? state.positions[employeeId]?.filter(pos => pos.organisationId !== orgId)
        : state.positions[employeeId];
    });
  }

  public employeePosition(employeeId: string, orgId: string): Signal<Position> {
    return computed(() =>
      orgId
        ? this.quickdrawProfileStateModel().positions[employeeId]?.find(pos => pos.organisationId === orgId)
        : undefined
    );
  }

  public loadEmployeeProfile(employeeId: string): Observable<void> {
    if (this.quickdrawProfileStateModel().employeeProfiles[employeeId]) {
      return of(undefined);
    }

    this.quickdrawProfileStateModel.update(state => ({
      ...state,
      employeeProfileLoading: true,
      employeeProfileLoadingError: undefined
    }));

    return this.employeeProfileService.getEmployeeProfile(employeeId).pipe(
      catchError(response => {
        const loadingError = response.status === HttpStatusCode.NotFound ? LoadingError.NotFound : LoadingError.Other;
        this.quickdrawProfileStateModel.update(state => ({
          ...state,
          employeeProfileLoading: false,
          employeeProfileLoadingError: loadingError
        }));
        throw response;
      }),
      map(quickProfile => this.employeeProfileLoaded(employeeId, quickProfile))
    );
  }

  private employeeProfileLoaded(employeeId: string, quickProfile: EmployeeQuickProfile): void {
    this.quickdrawProfileStateModel.update(state =>
      produce(state, draft => {
        draft.employeeProfileLoading = false;
        draft.employeeProfileLoadingError = undefined;
        draft.employeeProfiles[employeeId] = this.mapEmployeeProfile(quickProfile);
      })
    );
  }

  public loadPositions(employeeId: string): Observable<void> {
    if (this.quickdrawProfileStateModel().positions[employeeId]) {
      return of(undefined);
    }

    this.quickdrawProfileStateModel.update(state => ({ ...state, positionsLoading: true }));

    return this.employeePositionsService.getEmployeePositions(employeeId).pipe(
      this.httpHelper.handleError('Cannot get positions for employee'),
      // the generated client types this as Position[], but the backend returns PositionListModel ({ positions })
      map(positions => this.positionsLoaded(employeeId, positions as unknown as { positions: ApiPosition[] }))
    );
  }

  private positionsLoaded(employeeId: string, positions: { positions: ApiPosition[] }): void {
    this.quickdrawProfileStateModel.update(state =>
      produce(state, draft => {
        draft.positionsLoading = false;
        draft.positions[employeeId] = positions.positions.map(MappingHelpers.mapPosition);
      })
    );
  }

  private mapEmployeeProfile(profile: EmployeeQuickProfile): EmployeeProfile {
    return {
      employee: {
        ...MappingHelpers.mapEmployee(profile?.employeeBaseData),
        orgId: profile?.orgId,
        orgCode: profile?.orgCode,
        orgName: profile?.orgName,
        orgClass: profile?.orgClass,
        orgType: profile?.orgType,
        areCode: profile?.areCode,
        costCenter: profile?.costCenter
      },
      contactInfo: {
        email: profile?.email,
        mobile: profile?.mobileNumber,
        landline: profile?.landlineNumber
      },
      locationInfo: {
        timezone: profile?.location?.timezone,
        locationOffice: profile?.location?.locationOffice,
        regionIsoCode: profile?.location?.regionIsoCode,
        countryIsoCode: profile?.location?.countryIsoCode,
        city: profile?.location?.city
      },
      inCompanyManager: MappingHelpers.mapEmployee(profile?.inCompanyManager),
      lineManager: MappingHelpers.mapEmployee(profile?.lineManager),
      personalBusinessPartner: MappingHelpers.mapEmployee(profile?.personalBusinessPartner)
    };
  }
}
